/**
 * API Data Cache Service
 *
 * Handles caching of API data with versioning, expiry, and refresh logic
 */

import { dataStorage, metadataStorage } from '../storage/localForage';
import {
  fetchCategories,
  fetchAllItems,
  fetchAllBlueprints,
  fetchItemBlueprints,
  fetchIndustrySkills,
} from '../api/echoes';
import type {
  ItemCategory,
  Item,
  Blueprint,
  ItemBlueprint,
  IndustrySkill,
  CacheMetadata,
  CacheStatus,
  CacheRefreshProgress,
  CachedApiData,
  ItemPriceCache,
} from '@/types';
import { CacheKey, CACHE_VERSION, CACHE_EXPIRY } from '@/types';

/**
 * Create metadata for cached data
 */
function createMetadata(key: string, itemCount?: number): CacheMetadata {
  return {
    key,
    version: CACHE_VERSION,
    timestamp: Date.now(),
    itemCount,
  };
}

/**
 * Check if cached data is valid (not expired and correct version)
 */
function isValidCache(metadata: CacheMetadata | null): boolean {
  if (!metadata) return false;

  // Check version
  if (metadata.version !== CACHE_VERSION) {
    return false;
  }

  // Check expiry
  const age = Date.now() - metadata.timestamp;
  return age < CACHE_EXPIRY.API_DATA;
}

/**
 * Cache API Data Service
 */
export class ApiCacheService {
  /**
   * Get cached data for a specific key
   */
  private async getCached<T>(key: string): Promise<T | null> {
    const metadata = await metadataStorage.get<CacheMetadata>(key);

    if (!isValidCache(metadata)) {
      // Cache is invalid or expired, remove it
      await this.invalidate(key);
      return null;
    }

    return dataStorage.get<T>(key);
  }

  /**
   * Set cached data for a specific key
   */
  private async setCached<T>(key: string, data: T, itemCount?: number): Promise<void> {
    const metadata = createMetadata(key, itemCount);

    await Promise.all([dataStorage.set(key, data), metadataStorage.set(key, metadata)]);
  }

  /**
   * Invalidate (remove) cached data for a specific key
   */
  async invalidate(key: string): Promise<void> {
    await Promise.all([dataStorage.remove(key), metadataStorage.remove(key)]);
  }

  /**
   * Invalidate all cached API data
   */
  async invalidateAll(): Promise<void> {
    await Promise.all([dataStorage.clear(), metadataStorage.clear()]);
  }

  // ============================================================================
  // Categories
  // ============================================================================

  async getCategories(forceRefresh = false): Promise<ItemCategory[]> {
    if (!forceRefresh) {
      const cached = await this.getCached<ItemCategory[]>('categories');
      if (cached) return cached;
    }

    const data = await fetchCategories();
    await this.setCached('categories', data, data.length);
    return data;
  }

  // ============================================================================
  // Items
  // ============================================================================

  async getItems(
    forceRefresh = false,
    onProgress?: (page: number, total: number) => void
  ): Promise<Item[]> {
    if (!forceRefresh) {
      const cached = await this.getCached<Item[]>('items');
      if (cached) return cached;
    }

    const data = await fetchAllItems(onProgress);
    await this.setCached('items', data, data.length);
    return data;
  }

  // ============================================================================
  // Blueprints
  // ============================================================================

  async getBlueprints(
    forceRefresh = false,
    onProgress?: (page: number, total: number) => void
  ): Promise<Blueprint[]> {
    if (!forceRefresh) {
      const cached = await this.getCached<Blueprint[]>('blueprints');
      if (cached) return cached;
    }

    const data = await fetchAllBlueprints(onProgress);
    await this.setCached('blueprints', data, data.length);
    return data;
  }

  // ============================================================================
  // Item Blueprints (v2)
  // ============================================================================

  async getItemBlueprints(forceRefresh = false): Promise<ItemBlueprint[]> {
    if (!forceRefresh) {
      const cached = await this.getCached<ItemBlueprint[]>('itemBlueprints');
      if (cached) return cached;
    }

    const data = await fetchItemBlueprints();
    await this.setCached('itemBlueprints', data, data.length);
    return data;
  }

  // ============================================================================
  // Industry Skills (v2)
  // ============================================================================

  async getIndustrySkills(forceRefresh = false): Promise<IndustrySkill[]> {
    if (!forceRefresh) {
      const cached = await this.getCached<IndustrySkill[]>('industrySkills');
      if (cached) return cached;
    }

    const data = await fetchIndustrySkills();
    await this.setCached('industrySkills', data, data.length);
    return data;
  }

  // ============================================================================
  // Batch Operations
  // ============================================================================

  /**
   * Get all cached API data
   */
  async getAllData(forceRefresh = false): Promise<CachedApiData> {
    const [categories, items, blueprints, itemBlueprints, industrySkills] = await Promise.all([
      this.getCategories(forceRefresh),
      this.getItems(forceRefresh),
      this.getBlueprints(forceRefresh),
      this.getItemBlueprints(forceRefresh),
      this.getIndustrySkills(forceRefresh),
    ]);

    return {
      categories,
      items,
      blueprints,
      itemBlueprints,
      industrySkills,
    };
  }

  /**
   * Refresh all cached data with progress tracking
   */
  async refreshAll(onProgress?: (progress: CacheRefreshProgress) => void): Promise<CachedApiData> {
    // Invalidate existing cache
    await this.invalidateAll();

    onProgress?.({ step: 'Fetching categories', progress: 0 });
    const categories = await this.getCategories(true);

    onProgress?.({ step: 'Fetching industry skills', progress: 20 });
    const industrySkills = await this.getIndustrySkills(true);

    onProgress?.({ step: 'Fetching item blueprints', progress: 40 });
    const itemBlueprints = await this.getItemBlueprints(true);

    onProgress?.({ step: 'Fetching items', progress: 60 });
    const items = await this.getItems(true, (page, total) => {
      // Update progress as items are fetched
      const itemProgress = 60 + Math.min(20, (total / 3000) * 20);
      onProgress?.({
        step: `Fetching items (page ${page}, ${total} items)`,
        progress: itemProgress,
      });
    });

    onProgress?.({ step: 'Fetching blueprints', progress: 85 });
    const blueprints = await this.getBlueprints(true);

    onProgress?.({ step: 'Complete', progress: 100 });

    return {
      categories,
      items,
      blueprints,
      itemBlueprints,
      industrySkills,
    };
  }

  /**
   * Get cache status
   */
  async getStatus(): Promise<CacheStatus> {
    const keys = ['categories', 'items', 'blueprints', 'itemBlueprints', 'industrySkills'];
    const metadataList = await Promise.all(
      keys.map((key) => metadataStorage.get<CacheMetadata>(key))
    );

    const isPopulated = metadataList.every((meta) => meta !== null);
    const allValid = metadataList.every((meta) => isValidCache(meta));

    // Get oldest timestamp
    const timestamps = metadataList
      .filter((meta): meta is CacheMetadata => meta !== null)
      .map((meta) => meta.timestamp);
    const lastUpdated = timestamps.length > 0 ? Math.min(...timestamps) : null;

    // API data no longer expires automatically - isStale is always false for API data
    const isStale = false;

    // Get item counts
    const [categoriesMeta, itemsMeta, blueprintsMeta, itemBlueprintsMeta, skillsMeta] =
      metadataList;

    return {
      isPopulated,
      version: CACHE_VERSION,
      lastUpdated,
      itemCounts: {
        categories: categoriesMeta?.itemCount ?? 0,
        items: itemsMeta?.itemCount ?? 0,
        blueprints: blueprintsMeta?.itemCount ?? 0,
        itemBlueprints: itemBlueprintsMeta?.itemCount ?? 0,
        industrySkills: skillsMeta?.itemCount ?? 0,
      },
      isStale,
      isValid: allValid,
    };
  }

  /**
   * Check if cache needs refresh
   */
  async needsRefresh(): Promise<boolean> {
    const status = await this.getStatus();
    return !status.isPopulated || !status.isValid || status.isStale;
  }

  /**
   * Warm cache (preload data if not cached)
   */
  async warmCache(onProgress?: (progress: CacheRefreshProgress) => void): Promise<void> {
    const status = await this.getStatus();

    if (!status.isPopulated || !status.isValid) {
      await this.refreshAll(onProgress);
    }
  }

  /**
   * Get cached price for an item
   * @param itemId - Item ID
   * @returns Price in ISK, or null if not cached
   */
  async getCachedPrice(itemId: number): Promise<number | null> {
    const metadata = await metadataStorage.get<CacheMetadata>(CacheKey.ITEM_PRICES);

    // Check if price cache is valid (using shorter expiry for prices)
    if (metadata && metadata.version === CACHE_VERSION) {
      const age = Date.now() - metadata.timestamp;
      if (age < CACHE_EXPIRY.PRICES) {
        const priceCache = await dataStorage.get<ItemPriceCache>(CacheKey.ITEM_PRICES);
        if (priceCache) {
          const price = priceCache[itemId.toString()];
          if (price !== undefined) {
            return price;
          }
        }
      }
    }

    return null;
  }

  /**
   * Set cached price for an item
   * @param itemId - Item ID
   * @param price - Price in ISK
   */
  async setCachedPrice(itemId: number, price: number): Promise<void> {
    // Get existing cache
    const existingCache = await dataStorage.get<ItemPriceCache>(CacheKey.ITEM_PRICES);
    const priceCache: ItemPriceCache = existingCache || {};

    // Update price
    priceCache[itemId.toString()] = price;

    // Save cache
    await dataStorage.set(CacheKey.ITEM_PRICES, priceCache);

    // Update metadata
    const metadata = createMetadata(CacheKey.ITEM_PRICES, Object.keys(priceCache).length);
    await metadataStorage.set(CacheKey.ITEM_PRICES, metadata);
  }

  /**
   * Set multiple cached prices in batch
   * @param prices - Map of itemId -> price
   */
  async setCachedPrices(prices: Map<number, number>): Promise<void> {
    // Get existing cache
    const existingCache = await dataStorage.get<ItemPriceCache>(CacheKey.ITEM_PRICES);
    const priceCache: ItemPriceCache = existingCache || {};

    // Update prices
    prices.forEach((price, itemId) => {
      priceCache[itemId.toString()] = price;
    });

    // Save cache
    await dataStorage.set(CacheKey.ITEM_PRICES, priceCache);

    // Update metadata
    const metadata = createMetadata(CacheKey.ITEM_PRICES, Object.keys(priceCache).length);
    await metadataStorage.set(CacheKey.ITEM_PRICES, metadata);
  }

  /**
   * Clear price cache
   */
  async clearPriceCache(): Promise<void> {
    await dataStorage.remove(CacheKey.ITEM_PRICES);
    await metadataStorage.remove(CacheKey.ITEM_PRICES);
  }

  /**
   * Refresh all cached prices by clearing the price cache
   * Prices will be re-fetched on next BOM calculation
   */
  async refreshPrices(): Promise<void> {
    await this.clearPriceCache();
  }

  /**
   * Get price cache status
   */
  async getPriceCacheStatus(): Promise<{
    count: number;
    lastUpdated: number | null;
    isStale: boolean;
  }> {
    const metadata = await metadataStorage.get<CacheMetadata>(CacheKey.ITEM_PRICES);

    if (!metadata) {
      return { count: 0, lastUpdated: null, isStale: true };
    }

    const age = Date.now() - metadata.timestamp;
    const isStale = age > CACHE_EXPIRY.PRICES / 2; // Stale after half the expiry time

    return {
      count: metadata.itemCount ?? 0,
      lastUpdated: metadata.timestamp,
      isStale,
    };
  }
}

/**
 * Singleton instance
 */
export const apiCache = new ApiCacheService();
